import { AdminUser } from "@/components/admin/management";
import { ALL_COUNTRIES } from "@/common/constants/countries";

/**
 * Extracts initials from a name string
 * @param name - Full name (e.g., "John Doe")
 * @param fallback - Fallback initials if name is empty (default: "")
 * @returns Uppercase initials (e.g., "JD")
 */
export const getInitials = (name: string, fallback = ""): string =>
  name
    .split(" ")
    .map((n) => n[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || fallback;

/**
 * Formats a user's full name
 * @param user - Admin user object or null
 * @returns Formatted full name string
 */
export const getFullName = (user?: AdminUser | null): string =>
  user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "";

/**
 * Generate a random 64-character hex string starting with 0x
 */
export function generateRandomTxHash(): string {
  const characters = "0123456789abcdef";
  let hash = "0x";
  for (let i = 0; i < 64; i++) {
    hash += characters[Math.floor(Math.random() * characters.length)];
  }
  return hash;
}

/**
 * Formats a stake amount with comma separators
 * @param amount - The amount as string or number
 * @returns Formatted amount string (e.g., "100,000,000")
 */
export const formatStakeAmount = (
  amount: string | number | null | undefined,
): string => {
  if (amount == null || amount === "") return "—";

  let num: number;
  if (typeof amount === "string") {
    const normalized = amount.replace(/,/g, "").replace(/[^\d.-]/g, "");
    if (
      normalized === "" ||
      normalized === "-" ||
      normalized === "." ||
      normalized === "-."
    ) {
      return "—";
    }
    num = Number(normalized);
  } else {
    num = amount;
  }

  if (!Number.isFinite(num)) return "—";

  if (num < 1 && num > 0) {
    return toSignificantSafe(String(num), 4);
  }

  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Formats numeric values with grouping and up to 2 decimal places.
 * - Hides trailing `.00`
 * - Keeps decimals when present (e.g., 12.5 -> "12.5", 12.34 -> "12.34")
 * - Returns original input as string when value is not numeric
 */
export const formatBalanceWithOptionalDecimals = (
  value: string | number,
  decimals: number = 2,
): string => {
  const parsed =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : value;

  if (!Number.isFinite(parsed)) return String(value);

  return parsed.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a given amount into a millions representation (e.g., 100000000 -> "100M")
 * @param amount - The amount as string, number, bigint, null, or undefined
 * @returns Formatted amount string (e.g., "100M", "1.25M", "-50M") or "—" for invalid inputs
 */
export const formatAmountInMillions = (
  amount: string | number | bigint | null | undefined,
): string => {
  // ── 1. Null / undefined / empty ──────────────────────────────────────────
  if (amount == null || amount === "") return "—";
  // ── 2. BigInt handling ───────────────────────────────────────────────────
  let num: number;
  if (typeof amount === "bigint") {
    // BigInt can exceed Number.MAX_SAFE_INTEGER; lose precision gracefully
    num = Number(amount);
  } else {
    num = typeof amount === "string" ? parseFloat(amount.trim().replace(/,/g, "")) : amount;
  }
  // ── 3. NaN / Infinity guard ──────────────────────────────────────────────
  if (!isFinite(num) || isNaN(num)) return "—";
  // ── 4. Negative sign preservation ───────────────────────────────────────
  const sign = num < 0 ? "-" : "";
  const abs = Math.abs(num);
  // ── 5. Sub-million: use locale formatting ────────────────────────────────
  if (abs < 1_000_000) {
    if (abs < 1 && abs > 0) {
      // Use significant digits for small decimals to avoid rounding to zero
      return toSignificantSafe(String(num), 4);
    }
    return num.toLocaleString("en-US", {
      maximumFractionDigits: 2,
    });
  }
  // ── 6. Million conversion with floating-point correction ─────────────────
  //    parseFloat(toFixed(2)) strips trailing zeros: 100.50 → 100.5, 100.00 → 100
  const millions = parseFloat((abs / 1_000_000).toFixed(2));
  return `${sign}${millions}M`;
};
/**
 * Converts a 2-letter country code to a flag emoji
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "us", "bd")
 * @returns Flag emoji (e.g., "🇺🇸", "🇧🇩")
 */
export const getCountryFlag = (countryCode: string): string => {
  const code = countryCode.toUpperCase();
  const offset = 127397;
  return [...code]
    .map((c) => String.fromCodePoint(c.charCodeAt(0) + offset))
    .join("");
};

/**
 * Gets country name with flag emoji from country name
 * @param countryName - Full country name (e.g., "Bangladesh", "United States")
 * @returns Country name with flag (e.g., "Bangladesh 🇧🇩")
 */
export const getCountryWithFlag = (countryName: string): string => {
  const country = ALL_COUNTRIES.find(
    (c) => c.name.toLowerCase() === countryName.toLowerCase(),
  );

  if (!country) return countryName;
  return `${countryName} ${getCountryFlag(country.code)}`;
};

/**
 * Parses a value to extract the numeric part and check for a KROWN suffix.
 * @param value The value to parse (string or number).
 * @returns An object containing `numericValue` and `hasKrown` boolean.
 */
export const parseKrownValue = (
  value: string | number | undefined | null,
): { numericValue: string | number; hasKrown: boolean } => {
  if (value == null) return { numericValue: "", hasKrown: false };
  const KROWN_SUFFIX = " KROWN";
  if (typeof value === "string") {
    const hasKrown = value.endsWith(KROWN_SUFFIX);
    const numericValue = hasKrown
      ? value.slice(0, -KROWN_SUFFIX.length)
      : value;
    return { numericValue, hasKrown };
  }
  return { numericValue: value, hasKrown: false };
};

/**
 * Formats an ID with a prefix and the first 4 characters of a UUID.
 * @param prefix - The prefix string (e.g., "UB", "ST")
 * @param uuid - The UUID as a string or number
 * @returns Formatted ID (e.g., "UB-A1B2")
 */
export const formatPrefixedId = (
  prefix: string,
  uuid: string | number,
): string => {
  const id = String(uuid).slice(0, 4).toUpperCase();
  return `${prefix}-${id}`;
};

/**
 * Formats a number to a significant number with a specified number of digits.
 * @param value - The value to format (string or number).
 * @param digits - The number of digits to format the value to.
 * @returns Formatted value string (e.g., "12.345678901234567890")
 */
export const toSignificantSafe = (value: string, digits: number) => {
  return Number.parseFloat(value).toLocaleString("en-US", {
    useGrouping: false,
    maximumSignificantDigits: digits,
  });
};
